import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, 
  subMonths, isToday, startOfWeek, endOfWeek, addDays, subDays, 
  eachHourOfInterval, setHours, setMinutes, isWithinInterval, parseISO 
} from 'date-fns';
import { nb } from 'date-fns/locale';
import { useData } from '../../../contexts/DataContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { 
  FiArrowLeft, FiCalendar, FiClock, FiUser, FiSearch, FiCheck, FiX, 
  FiFilter, FiInfo, FiAlertCircle, FiChevronRight, FiEdit3, FiPhone, 
  FiMail, FiBriefcase, FiGrid, FiList, FiChevronLeft, FiSettings, FiCheckCircle, FiSlash, 
  FiVideo, FiMonitor
} = FiIcons;

const BookingOverview = ({ forceAgentId = null, role = 'superadmin' }) => {
  const navigate = useNavigate();
  const { bookings = [], sellers = [], partners = [], updateBooking, calendarSettings, updateCalendarSettings } = useData();
  
  const [viewMode, setViewMode] = useState('calendar'); 
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState(forceAgentId || 'all');
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());

  const statusOptions = [
    { value: 'pending', label: 'Venter', color: 'border-amber-500/50', badge: 'bg-amber-500/10 text-amber-600', icon: FiClock },
    { value: 'approved', label: 'Bekreftet', color: 'border-emerald-500/50', badge: 'bg-emerald-500/10 text-emerald-600', icon: FiCheckCircle },
    { value: 'completed', label: 'Fullført', color: 'border-blue-500/50', badge: 'bg-blue-500/10 text-blue-600', icon: FiCheck },
    { value: 'rejected', label: 'Avvist', color: 'border-red-500/50', badge: 'bg-red-500/10 text-red-600', icon: FiX },
    { value: 'cancelled', label: 'Kansellert', color: 'border-slate-500/50', badge: 'bg-slate-500/10 text-slate-600', icon: FiSlash }
  ];

  const platformMeta = {
    google_meet: { label: 'Google Meet', icon: FiVideo, color: 'text-blue-400' },
    teams: { label: 'Teams', icon: FiUser, color: 'text-indigo-400' },
    zoom: { label: 'Zoom', icon: FiMonitor, color: 'text-blue-500' }
  };

  const allAgents = useMemo(() => [
    ...sellers.map(s => ({ ...s, type: 'Selger' })),
    ...partners.map(p => ({ ...p, type: 'Partner' }))
  ], [sellers, partners]);

  const enrichedBookings = useMemo(() => {
    return (Array.isArray(bookings) ? bookings : []).map(b => {
      const agent = allAgents.find(a => a.id === b.sellerId || a.partner_id === b.sellerId);
      return { ...b, agentName: agent?.name || 'Ikke tildelt', agentType: agent?.type };
    });
  }, [bookings, allAgents]);

  const filteredBookings = useMemo(() => {
    return enrichedBookings.filter(b => {
      const matchesSearch = (b.customerName || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
                           (b.customerCompany || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || b.status === statusFilter;
      const matchesAgent = agentFilter === 'all' || b.sellerId === agentFilter;
      return matchesSearch && matchesStatus && matchesAgent;
    });
  }, [enrichedBookings, searchTerm, statusFilter, agentFilter]);

  const handleUpdate = async (id, updates) => {
    setIsUpdating(true);
    const result = await updateBooking(id, updates);
    if (!result.success) alert("Feil: " + result.error?.message);
    setIsUpdating(false);
    if (selectedBooking?.id === id) setSelectedBooking({ ...selectedBooking, ...updates });
  };

  const timeSlots = useMemo(() => {
    const hours = [];
    for(let i = 6; i <= 21; i++) hours.push(`${i.toString().padStart(2, '0')}:00`);
    return hours;
  }, []);

  const BookingCard = ({ booking }) => {
    const status = statusOptions.find(s => s.value === (booking.status || 'pending')) || statusOptions[0];
    const platform = platformMeta[booking.meetingPlatform || 'google_meet'];

    return (
      <motion.button 
        whileHover={{ scale: 1.02, y: -2, boxShadow: "0 10px 25px -5px rgba(30, 64, 175, 0.3)" }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setSelectedBooking(booking)}
        className={`w-full text-left rounded-xl shadow-md border border-slate-700/50 transition-all overflow-hidden relative group
          bg-gradient-to-br from-slate-800 to-slate-900 p-3`}
      >
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-1.5 text-blue-400 font-bold text-[10px] uppercase tracking-wider">
            <SafeIcon icon={FiClock} size={10} />
            {booking.time}
          </div>
          <div className={`px-1.5 py-0.5 rounded-md text-[8px] font-black uppercase tracking-tighter ${status.badge}`}>
            {status.label}
          </div>
        </div>

        <h4 className="text-white font-black text-[13px] leading-tight mb-2 group-hover:text-blue-300 transition-colors">
          {booking.customerCompany}
        </h4>
        
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-700/50">
          <div className="flex items-center gap-1.5 text-slate-400 text-[9px] font-bold uppercase truncate">
            <SafeIcon icon={FiUser} size={9} />
            {booking.agentName}
          </div>
          <div className="flex items-center gap-1 text-slate-500" title={platform.label}>
             <SafeIcon icon={platform.icon} size={10} className={platform.color} />
          </div>
        </div>
      </motion.button>
    );
  };

  const renderTimelineView = (days) => (
    <div className="bg-white rounded-[24px] shadow-xl border border-slate-200 overflow-hidden flex flex-col">
      <div className="p-8 border-b flex justify-between items-center bg-gradient-to-r from-[#0F172A] to-[#1E293B] text-white">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-400 mb-1">Operativ Oversikt</p>
          <h2 className="text-2xl font-black uppercase tracking-tight">
            {days.length === 1 
              ? format(days[0], 'EEEE d. MMMM yyyy', { locale: nb })
              : `${format(days[0], 'd. MMM', { locale: nb })} - ${format(days[days.length-1], 'd. MMM yyyy', { locale: nb })}`
            }
          </h2>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setCurrentDate(subDays(currentDate, days.length))} className="p-2.5 bg-white/5 hover:bg-white/10 rounded-xl transition-all border border-white/10"><SafeIcon icon={FiChevronLeft} /></button>
          <button onClick={() => setCurrentDate(new Date())} className="px-6 py-2 text-xs font-black uppercase bg-blue-600 hover:bg-blue-500 rounded-xl shadow-lg transition-all">I dag</button>
          <button onClick={() => setCurrentDate(addDays(currentDate, days.length))} className="p-2.5 bg-white/5 hover:bg-white/10 rounded-xl transition-all border border-white/10"><SafeIcon icon={FiChevronRight} /></button>
        </div>
      </div>
      
      <div className="flex-1 overflow-auto max-h-[750px] bg-[#F8FAFF]">
        <div className="grid" style={{ gridTemplateColumns: `100px repeat(${days.length}, 1fr)`, minWidth: `${days.length * 200 + 100}px` }}>
          <div className="sticky top-0 z-20 bg-slate-900/98 backdrop-blur-md border-b border-white/5 py-4"></div>
          {days.map(day => (
            <div key={day.toString()} className={`sticky top-0 z-20 bg-slate-900/98 backdrop-blur-md border-b border-white/5 py-4 text-center ${isToday(day) ? 'after:absolute after:bottom-0 after:left-0 after:right-0 after:h-1 after:bg-blue-500' : ''}`}>
              <p className="text-[10px] font-black uppercase text-slate-500 tracking-widest mb-1">{format(day, 'EEEE', { locale: nb })}</p>
              <p className={`text-lg font-black ${isToday(day) ? 'text-blue-400' : 'text-white'}`}>{format(day, 'd. MMM')}</p>
            </div>
          ))}

          {timeSlots.map(time => (
            <React.Fragment key={time}>
              <div className="border-b border-r border-slate-200 py-8 px-4 text-[11px] font-black text-slate-400 text-right sticky left-0 bg-white z-10">
                {time}
              </div>
              {days.map(day => {
                const dayBookings = filteredBookings.filter(b => isSameDay(new Date(b.date), day) && b.time.startsWith(time.split(':')[0]));
                const today = isToday(day);
                return (
                  <div key={`${day}-${time}`} className={`border-b border-r border-slate-200 relative min-h-[120px] transition-colors 
                    ${today ? 'bg-blue-50/30' : 'bg-white'}`}>
                    <div className="absolute inset-0 p-2 flex flex-col gap-2">
                      {dayBookings.map(b => <BookingCard key={b.id} booking={b} />)}
                    </div>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );

  const getCalendarContent = () => {
    switch(viewMode) {
      case 'day': return renderTimelineView([currentDate]);
      case 'week': 
        const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        return renderTimelineView([...Array(7)].map((_, i) => addDays(weekStart, i)));
      case 'workweek':
        const workWeekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
        return renderTimelineView([...Array(5)].map((_, i) => addDays(workWeekStart, i)));
      case 'calendar':
        const start = startOfWeek(startOfMonth(currentDate), { weekStartsOn: 1 });
        const end = endOfWeek(endOfMonth(currentDate), { weekStartsOn: 1 });
        const days = eachDayOfInterval({ start, end });
        return (
          <div className="bg-white rounded-[32px] shadow-2xl border border-slate-200 overflow-hidden">
            <div className="p-8 border-b flex justify-between items-center bg-[#0F172A] text-white">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-400 mb-1">Månedsoversikt</p>
                <h2 className="text-2xl font-black uppercase tracking-tight">{format(currentDate, 'MMMM yyyy', { locale: nb })}</h2>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setCurrentDate(subMonths(currentDate, 1))} className="p-2.5 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10"><SafeIcon icon={FiChevronLeft} /></button>
                <button onClick={() => setCurrentDate(new Date())} className="px-5 py-2 text-[10px] font-black uppercase bg-blue-600 rounded-xl">I dag</button>
                <button onClick={() => setCurrentDate(addMonths(currentDate, 1))} className="p-2.5 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10"><SafeIcon icon={FiChevronRight} /></button>
              </div>
            </div>
            <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50/50">
              {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map(d => (
                <div key={d} className="py-4 text-center text-[10px] font-black uppercase text-slate-400 tracking-[0.2em]">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {days.map((day, idx) => {
                const dayBookings = filteredBookings.filter(b => isSameDay(new Date(b.date), day));
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                const today = isToday(day);

                return (
                  <div key={idx} className={`min-h-[160px] border-b border-r border-slate-100 p-3 transition-all
                    ${!isCurrentMonth ? 'bg-slate-50 opacity-40' : today ? 'bg-blue-50/40 ring-1 ring-inset ring-blue-500/10' : 'bg-white'}`}>
                    <div className="flex justify-between items-start mb-3">
                      <span className={`text-[13px] font-black transition-all ${today ? 'w-8 h-8 bg-blue-600 text-white rounded-xl flex items-center justify-center shadow-lg' : isCurrentMonth ? 'text-slate-900' : 'text-slate-300'}`}>
                        {format(day, 'd')}
                      </span>
                      {dayBookings.length > 0 && <span className="text-[10px] font-black text-blue-600 bg-blue-100 px-2 py-0.5 rounded-lg">{dayBookings.length}</span>}
                    </div>
                    <div className="space-y-1.5">
                      {dayBookings.slice(0, 3).map(b => (
                        <button key={b.id} onClick={() => setSelectedBooking(b)} 
                          className="w-full text-left p-1.5 rounded-lg text-[10px] font-bold truncate border border-slate-200 bg-white hover:border-blue-300 hover:shadow-sm transition-all text-slate-700">
                          <span className="text-blue-600 mr-1">{b.time}</span> {b.customerCompany}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] font-sans">
      {role === 'superadmin' && (
        <header className="bg-white/80 backdrop-blur-md shadow-sm border-b border-slate-200 sticky top-0 z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="flex items-center gap-4">
                <button onClick={() => navigate('/superadmin')} className="p-3 hover:bg-slate-100 rounded-2xl transition-all border border-slate-200 shadow-sm"><SafeIcon icon={FiArrowLeft} /></button>
                <div>
                  <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2 uppercase tracking-tighter">
                    <SafeIcon icon={FiCalendar} className="text-blue-600" /> Booking-admin
                  </h1>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Salg og demo-oversikt</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex bg-slate-100 p-1 rounded-2xl border border-slate-200/50">
                  {[{ id: 'calendar', label: 'Måned' }, { id: 'workweek', label: 'Arbeidsuke' }, { id: 'week', label: 'Full uke' }, { id: 'day', label: 'Dag' }].map(mode => (
                    <button key={mode.id} onClick={() => setViewMode(mode.id)} className={`px-5 py-2 rounded-xl text-[10px] font-black uppercase transition-all ${viewMode === mode.id ? 'bg-white shadow-xl text-blue-600' : 'text-slate-400 hover:text-slate-600'}`} > {mode.label} </button>
                  ))}
                </div>
                <button onClick={() => setShowSettings(true)} className="p-3 bg-white text-slate-400 hover:text-blue-600 border border-slate-200 rounded-2xl shadow-sm"> <SafeIcon icon={FiSettings} /> </button>
              </div>
            </div>
          </div>
        </header>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="bg-white rounded-[24px] shadow-sm p-6 mb-10 flex flex-col lg:flex-row gap-4 items-center border border-slate-200">
          <div className="relative flex-1 group">
            <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500" />
            <input type="text" placeholder="Søk kundenavn eller bedrift..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-4 bg-slate-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 font-medium" />
          </div>
          <div className="flex gap-3">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-slate-50 border-none rounded-xl px-5 py-4 text-sm font-bold outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">Alle statuser</option>
              {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
        </div>
        {getCalendarContent()}
      </div>

      <AnimatePresence>
        {selectedBooking && (
          <div className="fixed inset-0 bg-[#0F172A]/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4" onClick={() => setSelectedBooking(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="bg-white w-full max-w-2xl rounded-[32px] shadow-2xl overflow-hidden border border-white/20" onClick={e => e.stopPropagation()}>
              <div className="bg-blue-600 p-10 text-white flex justify-between items-start">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-white/20 rounded-[24px] flex items-center justify-center backdrop-blur-md border border-white/30 shadow-xl"><SafeIcon icon={FiCalendar} size={36} /></div>
                  <div>
                    <h2 className="text-3xl font-black uppercase tracking-tighter">Møtebehandling</h2>
                    <p className="text-blue-100 text-sm font-bold uppercase tracking-widest mt-1">
                      {format(new Date(selectedBooking.date), 'EEEE dd.MM.yyyy', { locale: nb })} kl. {selectedBooking.time}
                    </p>
                  </div>
                </div>
                <button onClick={() => setSelectedBooking(null)} className="p-3 hover:bg-white/20 rounded-2xl transition-all"><SafeIcon icon={FiX} size={28} /></button>
              </div>

              <div className="p-10 grid md:grid-cols-2 gap-10 overflow-y-auto max-h-[60vh]">
                <div className="space-y-8">
                  <section>
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100 pb-3 mb-6">Møteinformasjon</h3>
                    <div className="space-y-5">
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400">
                          <SafeIcon icon={platformMeta[selectedBooking.meetingPlatform || 'google_meet'].icon} />
                        </div>
                        <div>
                          <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Plattform</p>
                          <p className="font-black text-slate-900 leading-tight">{platformMeta[selectedBooking.meetingPlatform || 'google_meet'].label}</p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-400"><SafeIcon icon={FiBriefcase} /></div>
                        <div><p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Bedrift</p><p className="font-black text-slate-900 leading-tight">{selectedBooking.customerCompany}</p></div>
                      </div>
                    </div>
                  </section>
                </div>

                <div className="space-y-8">
                  <section>
                    <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100 pb-3 mb-6">Administrasjon</h3>
                    <div className="space-y-6">
                      <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 ml-1">Status</label>
                        <select value={selectedBooking.status || 'pending'} onChange={(e) => handleUpdate(selectedBooking.id, { status: e.target.value })} disabled={isUpdating} className="w-full p-4 bg-slate-50 border-none rounded-2xl font-black text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 transition-all">
                          {statusOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                      </div>
                    </div>
                  </section>
                </div>

                <div className="md:col-span-2">
                  <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100 pb-3 mb-4">Melding fra kunde</h3>
                  <div className="bg-slate-50 p-8 rounded-[24px] border border-slate-100 font-medium text-slate-700 italic text-sm leading-relaxed shadow-inner">
                    {selectedBooking.notes || "Ingen tilleggshonorar eller melding spesifisert."}
                  </div>
                </div>
              </div>

              <div className="p-10 bg-slate-50 border-t border-slate-100 flex justify-end gap-3">
                <button onClick={() => setSelectedBooking(null)} className="px-10 py-4 bg-slate-900 text-white rounded-[20px] font-black uppercase tracking-widest shadow-xl hover:bg-black transition-all">Lukk</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BookingOverview;